/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import type { CompetitionResult } from '@sportninja/common/features/teamFinder/types'
import ShowMoreFooter from 'src/components/ts-components/ShowMoreFooter'
import CompetitionResultCard from './CompetitionResultCard'
import NoTeamFinderResults from './NoTeamFinderResults'
import ResultViewSwitcher from './ResultViewSwitcher'
import TeamFinderResultsSkeleton from './TeamFinderResultsSkeleton'
import TeamFinderTable from './TeamFinderTable'
import { useResultViewMode } from '../hooks/useResultViewMode'

interface Props {
  results: CompetitionResult[]
  isLoading: boolean
  /** A further page is available for the current search. */
  hasMore?: boolean
  /** A "Show More" page is in flight (results stay on screen). */
  isLoadingMore?: boolean
  onLoadMore?: () => void
}

const TeamFinderResults = ({
  results,
  isLoading,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: Props) => {
  'use memo'

  const { mode, setMode } = useResultViewMode()

  if (isLoading) {
    return <TeamFinderResultsSkeleton />
  }
  if (results.length === 0) {
    return <NoTeamFinderResults />
  }

  return (
    <div css={wrapperStyle}>
      <div css={toolbarStyle}>
        <ResultViewSwitcher mode={mode} onChange={setMode} />
      </div>

      {mode === 'card' ? (
        <div css={gridStyle}>
          {results.map((r) => (
            <CompetitionResultCard key={r.id} result={r} />
          ))}
        </div>
      ) : (
        <TeamFinderTable results={results} />
      )}

      {/* Outside the view branch: paging belongs to the search, not to how the
          results are being displayed, so both views page identically. */}
      {onLoadMore && (
        <ShowMoreFooter
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          onLoadMore={onLoadMore}
          data-testid='team-finder-show-more'
        />
      )}
    </div>
  )
}

export default TeamFinderResults

const wrapperStyle = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
`
// Right-aligned above the results, matching the header-action row convention.
const toolbarStyle = css`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
`
const gridStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
`
